import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { fetchVendorDashboardStats, setDashboardTimeRange, clearError, clearStats } from "./dashboard.slice";
import { VendorDashboardTimeRange } from "./dashboard.api";

export const useVendorDashboard = () => {
  const dispatch = useAppDispatch();
  const dashboardState = useAppSelector((state) => state.vendorDashboard);
  const { stats, dashboardTimeRange, loading, error, lastUpdated } = dashboardState || {
    stats: null,
    dashboardTimeRange: "30d" as VendorDashboardTimeRange,
    loading: false,
    error: null,
    lastUpdated: null,
  };

  // Load dashboard stats on mount (uses current dashboardTimeRange from state)
  useEffect(() => {
    dispatch(fetchVendorDashboardStats(undefined));
  }, [dispatch]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchVendorDashboardStats(undefined));
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [dispatch]);

  const refreshStats = useCallback(() => {
    dispatch(fetchVendorDashboardStats(undefined));
  }, [dispatch]);

  const setTimeRange = useCallback(
    (range: VendorDashboardTimeRange) => {
      dispatch(setDashboardTimeRange(range));
      dispatch(fetchVendorDashboardStats(range));
    },
    [dispatch]
  );

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleClearStats = useCallback(() => {
    dispatch(clearStats());
  }, [dispatch]);

  return {
    stats,
    dashboardTimeRange,
    setDashboardTimeRange: setTimeRange,
    loading,
    error,
    lastUpdated,
    refreshStats,
    clearError: handleClearError,
    clearStats: handleClearStats,
  };
};
